import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useRouter } from "next/router";
import React from "react";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import type { DealEstimationContext } from "~/models/DealEstimate";
import type { DealEstimationHeader } from "~/models/DealEstimationHeader";
import { estimationRequestSide } from "~/models/DealEstimationHeader";
import type { ItandiAccount } from "~/models/ItandiAccount";
import { hasPermissionToConstructionStatus } from "~/models/Staff";
import { IN_CONSTRUCTION, isOwnStatus, SEND_ESTIMATE } from "~/models/Status";
import { PATH } from "~/pages/deals/[id]";
import { PATH as VENDOR_PATH } from "~/pages/vendors/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { ConfirmModal } from "../parts/modal/ConfirmModal";
import Styles from "./ConstructionFooter.module.scss";

type Props = {
	acceptRejectDisabled?: boolean;
	account?: ItandiAccount | null;
	companyType: "client" | "vendor" | "partner";
	deal: Deal;
	dealEstimationContext?: DealEstimationContext | null;
	dealEstimationHeader: DealEstimationHeader | null;
	forwardDisabled?: boolean;
	handleAcceptForward?:
		| ((dealId: number, statusId: number) => Promise<NskResponse>)
		| null;
	handleMutate: () => Promise<void>;
	handleUpdateAcceptStatus: (
		dealId: number,
		dealEstimationHeaderId: number
	) => Promise<NskResponse>;
	handleUpdateRejectStatus: (
		dealId: number,
		dealEstimationHeaderId: number
	) => Promise<NskResponse>;
	isAdmin: boolean;
};

export function ConstructionConfirmedFooter({
	deal,
	isAdmin,
	companyType,
	dealEstimationHeader,
	handleUpdateAcceptStatus,
	handleUpdateRejectStatus,
	handleMutate,
	handleAcceptForward = null,
	dealEstimationContext = null,
	account = null,
	forwardDisabled = false,
	acceptRejectDisabled = false
}: Props) {
	const { notify } = useNotification();
	const router = useRouter();
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const {
		isOpen: isOpenUpdateStatus,
		handleOpen: handleOpenUpdateStatus,
		handleClose: handleCloseUpdateStatus
	} = useOpenHandler();
	const {
		isOpen: isOpenAccept,
		handleOpen: handleOpenAccept,
		handleClose: handleCloseAccept
	} = useOpenHandler();
	const {
		isOpen: isOpenReject,
		handleOpen: handleOpenReject,
		handleClose: handleCloseReject
	} = useOpenHandler();
	const onClickUpdateStatus = async (): Promise<void> => {
		if (handleAcceptForward === null) return;
		setIsSubmitting(true);
		const result = await handleAcceptForward(deal.id, IN_CONSTRUCTION);
		if (isSuccess(result)) {
			notify({ content: "ステータスを進めました。", variant: "success" });
			await router.push(
				companyType === "client" ? PATH(deal.id) : VENDOR_PATH(deal.id)
			);
			setIsSubmitting(false);
			return;
		}
		handleCloseUpdateStatus();
		setIsSubmitting(false);
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	// 申請中の見積りがない場合は見積り確認画面でステータス進行のみできるようにする
	if (
		dealEstimationHeader === null &&
		deal.statusId === SEND_ESTIMATE &&
		dealEstimationContext &&
		account
	) {
		return (
			<div className={Styles.ButtonArea}>
				<Flex
					gap="8px"
					justifyContent="right"
					marginTop="16px"
					padding="16px 8px"
				>
					<Button
						disabled={
							forwardDisabled ||
							isAdmin ||
							!hasPermissionToConstructionStatus(account) ||
							dealEstimationContext.dealConstructions.length === 0
						}
						onClick={handleOpenUpdateStatus}
						variant="primary"
					>
						ステータスを進める
					</Button>
					<ConfirmModal
						confirmType="save"
						handleAccept={onClickUpdateStatus}
						handleCancel={handleCloseUpdateStatus}
						isOpen={isOpenUpdateStatus}
					>
						ステータスを進めてよろしいですか？
						<Alert marginTop=".5rem" variant="info">
							確定ボタンを押すと工事の手配へ移ります。
						</Alert>
					</ConfirmModal>
				</Flex>
			</div>
		);
	}
	if (
		// 非共有案件で承認等の操作は行わない
		isOwnStatus(deal.statusId) ||
		// 実施会社は承認等の操作は行わない
		companyType === "partner" ||
		dealEstimationHeader === null ||
		estimationRequestSide(deal, companyType)
	) {
		return null;
	}
	// 工事完了待ちの時の反映（承認）は重い操作
	const lightConfirmed =
		companyType === "client" && deal.statusId === SEND_ESTIMATE;
	const onClickAccept = async (): Promise<void> => {
		setIsSubmitting(true);
		const result = await handleUpdateAcceptStatus(
			deal.id,
			dealEstimationHeader.id
		);
		if (isSuccess(result)) {
			await handleMutate();
			handleCloseAccept();
			setIsSubmitting(false);
			notify({ content: "申請を承認しました。", variant: "success" });
			return;
		}
		handleCloseAccept();
		setIsSubmitting(false);
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	const onClickReject = async (): Promise<void> => {
		setIsSubmitting(true);
		const result = await handleUpdateRejectStatus(
			deal.id,
			dealEstimationHeader.id
		);
		if (isSuccess(result)) {
			await handleMutate();
			notify({ content: "申請を棄却しました。", variant: "success" });
			handleCloseReject();
			setIsSubmitting(false);
			return;
		}
		handleCloseReject();
		setIsSubmitting(false);
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	const onClickSubmit = async (): Promise<void> => {
		if (handleAcceptForward === null) return;
		setIsSubmitting(true);
		const response = await handleUpdateAcceptStatus(
			deal.id,
			dealEstimationHeader.id
		);
		if (isSuccess(response)) {
			const result = await handleAcceptForward(deal.id, IN_CONSTRUCTION);
			if (isSuccess(result)) {
				await handleMutate();
				notify({
					content: "申請を承認してステータスを進めました。",
					variant: "success"
				});
				await router.push(
					companyType === "client" ? PATH(deal.id) : VENDOR_PATH(deal.id)
				);
			} else {
				await handleMutate();
				notify({ content: "申請を承認しました。", variant: "success" });
				notify({
					content: result.message ?? "エラーが発生しました。",
					variant: "error"
				});
			}
			handleClose();
			setIsSubmitting(false);
			return;
		}
		handleClose();
		setIsSubmitting(false);
		notify({
			content: response.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	return (
		<div className={Styles.ButtonArea}>
			<Flex
				gap="8px"
				justifyContent="right"
				marginTop="16px"
				padding="16px 8px"
			>
				<Button
					disabled={acceptRejectDisabled || isAdmin || isSubmitting}
					onClick={handleOpenReject}
					variant="tertiary"
				>
					申請を棄却する
				</Button>
				<Button
					disabled={acceptRejectDisabled || isAdmin || isSubmitting}
					onClick={handleOpenAccept}
					variant={lightConfirmed ? "secondary" : "primary"}
				>
					{lightConfirmed ? "申請を反映する" : "申請を承認する"}
				</Button>
				{account &&
				hasPermissionToConstructionStatus(account) &&
				deal.statusId === SEND_ESTIMATE ? (
					<Button
						disabled={
							acceptRejectDisabled || isAdmin || isSubmitting || forwardDisabled
						}
						onClick={handleOpen}
						variant="primary"
					>
						反映してステータスを進める
					</Button>
				) : null}
			</Flex>
			<ConfirmModal
				confirmType="save"
				handleAccept={onClickReject}
				handleCancel={handleCloseReject}
				isOpen={isOpenReject}
			>
				見積りの申請を棄却してよろしいですか？
				<Alert marginTop=".5rem" variant="warning">
					申請された情報が棄却されます。内容は反映されません。
				</Alert>
			</ConfirmModal>
			<ConfirmModal
				confirmType="save"
				handleAccept={onClickAccept}
				handleCancel={handleCloseAccept}
				isOpen={isOpenAccept}
			>
				{lightConfirmed
					? "見積りの申請を反映してよろしいですか？"
					: "見積りの申請を承認してよろしいですか？"}
				<Alert marginTop=".5rem" variant="warning">
					{lightConfirmed
						? "申請された情報が反映されます。工事完了待ちに進めるまで発注扱いとはなりません。"
						: "工事完了待ちステータスの場合、見積りの内容が施工対象となります。"}
				</Alert>
			</ConfirmModal>
			<ConfirmModal
				confirmType="save"
				handleAccept={onClickSubmit}
				handleCancel={handleClose}
				isOpen={isOpen}
			>
				見積りの申請を反映し、ステータスを進めますか？
				<Alert marginTop=".5rem" variant="warning">
					申請された情報が反映され、工事完了待ちへステータスが変更されます。
					<br />
					確定ボタンを押すと工事の手配へ移ります。
				</Alert>
			</ConfirmModal>
		</div>
	);
}
